import React, { useEffect } from 'react'
import { ArrowLeft, Shield, Lock, Eye, Database, UserCheck } from 'lucide-react'

interface PrivacyPolicyProps {
  onBack?: () => void
}

export const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ onBack }) => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-primary via-dark-secondary to-dark-tertiary p-4">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-primary-600/10 to-transparent rounded-full blur-3xl" />
        <div className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-to-tl from-primary-500/10 to-transparent rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          {onBack && (
            <button
              onClick={onBack}
              className="
                inline-flex items-center space-x-2 mb-6
                text-light-tertiary hover:text-light-primary
                transition-colors duration-200
                focus:outline-none focus:ring-2 focus:ring-primary-500/50 rounded-lg p-2
              "
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back</span>
            </button>
          )}
          
          <div className="flex items-center space-x-4 mb-4">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-primary-600 to-primary-500 rounded-xl shadow-lg">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-light-primary">Privacy Policy</h1>
              <p className="text-light-tertiary">BLP Assistant - Listening Glass, LLC</p>
            </div>
          </div>
          
          <div className="bg-primary-500/10 border border-primary-500/30 rounded-lg p-4">
            <p className="text-sm text-primary-300">
              <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>
        </div>

        {/* Privacy Policy Content */}
        <div className="
          bg-gradient-to-br from-dark-secondary/80 to-dark-tertiary/60
          backdrop-blur-lg 
          border border-dark-tertiary/30 
          rounded-2xl 
          p-8 
          shadow-xl
          prose prose-invert max-w-none
        ">
          <div className="space-y-8 text-light-secondary">
            
            <section>
              <h2 className="text-xl font-semibold text-light-primary mb-4 flex items-center space-x-2">
                <Lock className="w-5 h-5 text-primary-400" />
                <span>1. Introduction</span>
              </h2>
              <p className="mb-4">
                Listening Glass, LLC ("we", "our", or "us") respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our BLP Assistant application ("Service").
              </p>
              <p>
                By using our Service, you consent to the data practices described in this Privacy Policy. If you do not agree with the practices described in this Privacy Policy, please do not use our Service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-light-primary mb-4 flex items-center space-x-2">
                <Database className="w-5 h-5 text-primary-400" />
                <span>2. Information We Collect</span>
              </h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-light-primary mb-2">Personal Information</h3>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li><strong>Account Information:</strong> Email address, password (encrypted), account preferences</li>
                    <li><strong>Profile Information:</strong> Name, professional details (if provided)</li>
                    <li><strong>Authentication Data:</strong> Login sessions, security tokens</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-light-primary mb-2">Usage Information</h3>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li><strong>Chat Interactions:</strong> Questions asked, responses received, conversation history</li>
                    <li><strong>Application Usage:</strong> Features used, time spent, interaction patterns</li>
                    <li><strong>Technical Data:</strong> IP address, browser type, device information, operating system</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-light-primary mb-2">Automatically Collected Information</h3>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li><strong>Log Data:</strong> Access logs, error logs, performance metrics</li>
                    <li><strong>Analytics Data:</strong> Usage statistics, feature adoption, user flows</li>
                    <li><strong>Security Data:</strong> Authentication attempts, security events</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-light-primary mb-4 flex items-center space-x-2">
                <UserCheck className="w-5 h-5 text-primary-400" />
                <span>3. How We Use Your Information</span>
              </h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-light-primary mb-2">Service Provision</h3>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Authenticate users and maintain account security</li>
                    <li>Provide AI-powered BLP assistance and responses</li>
                    <li>Maintain conversation history and context</li>
                    <li>Customize user experience based on preferences</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-light-primary mb-2">Service Improvement</h3>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Analyze usage patterns to improve functionality</li>
                    <li>Enhance AI model performance and accuracy</li>
                    <li>Develop new features and capabilities</li>
                    <li>Optimize system performance and reliability</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-light-primary mb-2">Communication</h3>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Send important service updates and announcements</li>
                    <li>Respond to user inquiries and support requests</li>
                    <li>Provide security notifications when necessary</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-light-primary mb-4">4. SMS Notifications</h2>

              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-light-primary mb-2">Admin Notification System</h3>
                  <p className="mb-3">
                    Our Service includes an optional SMS notification system for administrators to receive alerts about important account events (trial starts, subscription payments, cancellations).
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-light-primary mb-2">Opt-In and Consent</h3>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>SMS notifications are <strong>opt-in only</strong> - you must explicitly provide your phone number in the admin dashboard</li>
                    <li>By providing your phone number, you consent to receive operational SMS notifications</li>
                    <li>Message frequency varies based on customer account activity (typically low volume)</li>
                    <li>Standard message and data rates may apply from your carrier</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-light-primary mb-2">Opt-Out and Control</h3>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Reply <strong>STOP</strong> to any SMS notification to unsubscribe from all messages</li>
                    <li>Reply <strong>HELP</strong> for assistance</li>
                    <li>You can also disable notifications or change your phone number in the admin dashboard</li>
                    <li>Individual notification types can be toggled on/off in your preferences</li>
                  </ul>
                </div>

                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                  <p className="text-green-300 font-medium mb-2">SMS Data Protection Guarantee</p>
                  <p className="text-green-200 text-sm">
                    Your mobile phone number and SMS messaging information <strong>will NOT be sold, shared, or used for marketing purposes</strong>. SMS notifications are strictly for operational account alerts to authorized administrators.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-light-primary mb-2">SMS Service Provider</h3>
                  <p className="text-sm">
                    SMS notifications are delivered via Telnyx, a third-party telecommunications provider. Your phone number is securely stored and transmitted only for the purpose of delivering operational notifications.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-light-primary mb-4">5. Information Sharing and Disclosure</h2>
              
              <div className="space-y-4">
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                  <p className="text-green-300 font-medium mb-2">We do NOT sell your personal information to third parties.</p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-light-primary mb-2">Limited Sharing Scenarios</h3>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Service Providers:</strong> Trusted third-party services (hosting, authentication, analytics) under strict data protection agreements</li>
                    <li><strong>Legal Requirements:</strong> When required by law, court order, or government request</li>
                    <li><strong>Business Transfers:</strong> In connection with mergers, acquisitions, or asset sales (with continued privacy protection)</li>
                    <li><strong>Safety and Security:</strong> To protect rights, property, or safety of users or others</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-light-primary mb-4">6. Data Security and Protection</h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-light-primary mb-2">Security Measures</h3>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li><strong>Encryption:</strong> Data encrypted in transit and at rest</li>
                    <li><strong>Authentication:</strong> Secure user authentication via Supabase</li>
                    <li><strong>Access Controls:</strong> Role-based access and authorization</li>
                    <li><strong>Monitoring:</strong> Continuous security monitoring and threat detection</li>
                    <li><strong>Regular Audits:</strong> Periodic security assessments and updates</li>
                  </ul>
                </div>

                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                  <p className="text-yellow-300 text-sm">
                    <strong>Important Note:</strong> While we implement industry-standard security measures, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-light-primary mb-4 flex items-center space-x-2">
                <Eye className="w-5 h-5 text-primary-400" />
                <span>7. Your Privacy Rights</span>
              </h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-light-primary mb-2">Account Management</h3>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li><strong>Access:</strong> View and download your personal information</li>
                    <li><strong>Update:</strong> Modify or correct your account information</li>
                    <li><strong>Delete:</strong> Request deletion of your account and data</li>
                    <li><strong>Export:</strong> Obtain a copy of your data in a portable format</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-light-primary mb-2">Communication Preferences</h3>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Opt out of non-essential communications</li>
                    <li>Choose notification preferences</li>
                    <li>Control marketing communications (if any)</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-light-primary mb-4">8. Data Retention</h2>
              <div className="space-y-3">
                <p><strong>Account Data:</strong> Retained while your account is active and for a reasonable period after deletion for legal compliance.</p>
                <p><strong>Chat History:</strong> Stored to maintain service functionality; you can delete individual conversations.</p>
                <p><strong>Analytics Data:</strong> Aggregated, anonymized data may be retained indefinitely for service improvement.</p>
                <p><strong>Legal Obligations:</strong> Some data may be retained longer as required by applicable laws.</p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-light-primary mb-4">9. Children's Privacy</h2>
              <p>
                Our Service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us so we can delete such information.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-light-primary mb-4">10. International Data Transfers</h2>
              <p>
                Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy and applicable data protection laws.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-light-primary mb-4">11. Changes to This Privacy Policy</h2>
              <div className="space-y-3">
                <p>We may update this Privacy Policy from time to time. When we do:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>We will post the updated Privacy Policy on this page</li>
                  <li>We will update the "Last Updated" date</li>
                  <li>For significant changes, we may provide additional notice</li>
                  <li>Your continued use constitutes acceptance of the updated Privacy Policy</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-light-primary mb-4">12. Contact Us</h2>
              <div className="bg-dark-secondary/50 rounded-lg p-4 border border-dark-tertiary/30">
                <p className="mb-2"><strong>Listening Glass, LLC</strong></p>
                <p className="text-sm text-light-tertiary mb-3">
                  If you have questions about this Privacy Policy or our data practices, please contact us through the appropriate channels provided in the application.
                </p>
                <div className="text-xs text-light-tertiary space-y-2">
                  <p><strong>Data Protection Requests:</strong> For requests related to your personal information (access, correction, deletion), please use the account settings in the application or contact us directly.</p>
                  <p><strong>Trademark Notice:</strong> The Beneficiary Liquidity Plan (BLP) is a registered trademark and is used with permission of Phil Graham, creator of the BLP.</p>
                </div>
              </div>
            </section>

          </div>
        </div>
      </div>
    </div>
  )
}